import { Box } from '@mui/material';
import { KeyboardEvent, useMemo, useState } from 'react';
import { LuggageDataType } from '../../types/common';

export type QuestionsByKey = Record<string, unknown> | undefined;

export type IndicatorSection = {
  title: string;
  indicators: string[];
};

type Indicator = {
  index: number;
  text: string;
};

type AiReasoningTextProps = {
  questionsByKey?: QuestionsByKey;
  questionTitle?: string;
  reasoning?: string;
};

const OCCUPANCY_STATUS_QUESTION_KEYS = new Set([
  'property.preservationoccupancystatus',
  'wo.foreclosure.wells.210078whatistheoccupancystatus',
  'property.occupancystatus',
  'wo.condition.wells.215829dropdownlistdata',
  'wo.condition.usbank.occupancystatus',
  'wo.foreclosure.usbank.occupancystatus',
]);

const INDICATOR_KEY_PATTERN =
  /^occupancydetect\.(occupiedindicator|vacantindicator)\.(\d+)$/i;

const isOccupancyStatusQuestion = (questionTitle?: string) => {
  if (!questionTitle) return false;

  return OCCUPANCY_STATUS_QUESTION_KEYS.has(questionTitle.toLowerCase());
};

export const extractAnswerStrings = (value: unknown): string[] => {
  if (!value) return [];

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((element) => extractAnswerStrings(element));
  }

  if (typeof value === 'object') {
    const answerValue = value as {
      Answer?: unknown;
      answer?: unknown;
      Answers?: unknown;
      answers?: unknown;
      Value?: unknown;
      value?: unknown;
    };

    return [
      ...extractAnswerStrings(answerValue.Answer),
      ...extractAnswerStrings(answerValue.answer),
      ...extractAnswerStrings(answerValue.Answers),
      ...extractAnswerStrings(answerValue.answers),
      ...extractAnswerStrings(answerValue.Value),
      ...extractAnswerStrings(answerValue.value),
    ];
  }

  return [];
};

export const getReasoningFromQuestions = (
  questionsByKey: QuestionsByKey,
  questionTitle?: string
) => {
  if (!questionsByKey || !questionTitle) return;

  return extractAnswerStrings(
    questionsByKey[`Ai.Reasoning.${questionTitle}`]
  )[0];
};

const toSortedIndicatorText = (indicators: Indicator[]) =>
  indicators.sort((a, b) => a.index - b.index).map(({ text }) => text);

export const getLuggageQuestions = (
  luggage: LuggageDataType
): Record<string, unknown> => {
  const l = luggage['WORKORDER'] || {};
  const questions = l?.questions;
  const questionsByKey: Record<string, unknown> = {};
  if (!questions) return questionsByKey;
  Object.assign(questionsByKey, questions);
  return questionsByKey;
};

const getMatchingKeys = (
  questionsByKey: QuestionsByKey,
  matcher: (key: string) => boolean
): string[] => {
  if (!questionsByKey) return [];

  const matchingKeys: string[] = [];

  for (const key of Object.keys(questionsByKey)) {
    if (matcher(key)) {
      matchingKeys.push(key);
    }
  }

  return matchingKeys.sort();
};

export const getIndicatorSections = (
  questionsByKey: QuestionsByKey
): IndicatorSection[] => {
  if (!questionsByKey) return [];

  const occupiedIndicators: Indicator[] = [];
  const vacantIndicators: Indicator[] = [];

  for (const [key, value] of Object.entries(questionsByKey)) {
    const match = key.match(INDICATOR_KEY_PATTERN);

    if (!match) continue;

    const [, indicatorType, indicatorIndex] = match;
    const indicatorTexts = extractAnswerStrings(value);
    const indicatorList =
      indicatorType.toLowerCase() === 'occupiedindicator'
        ? occupiedIndicators
        : vacantIndicators;

    for (const [offset, text] of indicatorTexts.entries()) {
      indicatorList.push({
        index: Number(indicatorIndex) + offset / 1000,
        text,
      });
    }
  }

  return [
    {
      title: 'Occupied Indicators:',
      indicators: toSortedIndicatorText(occupiedIndicators),
    },
    {
      title: 'Vacant Indicators:',
      indicators: toSortedIndicatorText(vacantIndicators),
    },
  ].filter((section) => section.indicators.length > 0);
};

const AiReasoningText = ({
  questionsByKey,
  questionTitle,
  reasoning,
}: AiReasoningTextProps) => {
  const [expandedText, setExpandedText] = useState(false);

  const reasoningFromQuestions = useMemo(
    () => getReasoningFromQuestions(questionsByKey, questionTitle),
    [questionsByKey, questionTitle]
  );

  const shouldShowOccupancyIndicators =
    isOccupancyStatusQuestion(questionTitle);

  const indicatorSections = useMemo(
    () =>
      shouldShowOccupancyIndicators ? getIndicatorSections(questionsByKey) : [],
    [questionsByKey, shouldShowOccupancyIndicators]
  );

  const reasoningText = reasoningFromQuestions ?? reasoning;

  const hasIndicatorSections = indicatorSections.length > 0;
  const hasReasoningText = Boolean(reasoningText?.trim());

  if (!hasIndicatorSections && !hasReasoningText) return null;

  const toggleExpandedText = () => {
    setExpandedText((currentValue) => !currentValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpandedText();
    }
  };

  return (
    <Box
      component='div'
      role='button'
      tabIndex={0}
      aria-expanded={expandedText}
      onClick={toggleExpandedText}
      onKeyDown={handleKeyDown}
      sx={{
        fontFamily: 'Montserrat',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: '14.63px',
        color: '#343330',
        cursor: 'pointer',
        textAlign: 'left',
        textTransform: 'none',
        display: expandedText ? 'block' : '-webkit-box',
        WebkitLineClamp: expandedText ? 'unset' : 5,
        WebkitBoxOrient: 'vertical',
        overflow: expandedText ? 'visible' : 'hidden',
        textOverflow: 'ellipsis',
        wordBreak: 'break-word',
        hyphens: 'auto',
      }}
    >
      {hasIndicatorSections ? (
        <Box component='span' sx={{ display: 'block' }}>
          {indicatorSections.map((section) => (
            <Box
              component='span'
              key={section.title}
              sx={{ display: 'block', mb: 1.5 }}
            >
              <Box component='strong' sx={{ display: 'block', mb: 1 }}>
                {section.title}
              </Box>
              <Box component='ol' sx={{ m: 0, pl: 2.5 }}>
                {section.indicators.map((indicator, index) => (
                  <Box
                    component='li'
                    key={`${section.title}-${index}`}
                    sx={{ mb: 1 }}
                  >
                    {indicator}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        reasoningText
      )}
    </Box>
  );
};

export default AiReasoningText;
